import { useMemo } from "react";
import { useLeadershipDashboardAccess } from "apps/project-iq/hooks/useLeadershipDashboardAccess";
import { usePortfolios } from "apps/project-iq/hooks/usePortfolios";
import { usePortfolioDashboardWorkspaces } from "apps/project-iq/hooks/usePortfolioDashboardWorkspaces";
import { groupWorkspacesByPortfolio } from "apps/project-iq/utils/groupedWorkspaceDashboardUtils";
import { GroupedWorkspaceDashboard } from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard";
import {
  PORTFOLIO_ACCESS_DENIED_MESSAGE,
  PORTFOLIO_ACCESS_ERROR_MESSAGE,
  PORTFOLIO_DATA_ERROR_MESSAGE,
  PORTFOLIO_GROUP_EMPTY_STATE_MESSAGE,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.constants";

const PAGE_TITLE = "Portfolios Dashboard";
const PAGE_DESCRIPTION =
  "Roll-up status across every portfolio, workspace, and delivery workstream.";

export const PortfoliosDashboardPage = (): JSX.Element => {
  const {
    hasPortfolioAccess,
    hasExecutiveAccess,
    portfolioIds,
    isLoading: isAccessLoading,
    isPortfolioAccessError,
    isExecutiveAccessError,
  } = useLeadershipDashboardAccess();

  const {
    portfolios,
    isLoading: isPortfoliosLoading,
    isError: isPortfoliosError,
  } = usePortfolios();

  const {
    workspaces,
    isLoading: isWorkspacesLoading,
    isRefreshing: isWorkspacesRefreshing,
    isError: isWorkspacesError,
  } = usePortfolioDashboardWorkspaces({ isEnabled: hasPortfolioAccess });

  const accessiblePortfolios = useMemo(
    () =>
      hasExecutiveAccess
        ? portfolios
        : portfolios.filter((portfolio) => portfolioIds.includes(portfolio.id)),
    [hasExecutiveAccess, portfolios, portfolioIds],
  );

  const portfolioGroups = useMemo(
    () => groupWorkspacesByPortfolio(accessiblePortfolios, workspaces),
    [accessiblePortfolios, workspaces],
  );

  const isAccessCheckError = isPortfolioAccessError || isExecutiveAccessError;
  const isDataLoading =
    hasPortfolioAccess &&
    (isPortfoliosLoading || isWorkspacesLoading || isWorkspacesRefreshing);
  const isDataError = isPortfoliosError || isWorkspacesError;

  return (
    <GroupedWorkspaceDashboard
      pageTitle={PAGE_TITLE}
      pageDescription={PAGE_DESCRIPTION}
      isAccessLoading={isAccessLoading}
      isAccessCheckError={isAccessCheckError}
      accessCheckErrorMessage={PORTFOLIO_ACCESS_ERROR_MESSAGE}
      hasAccess={hasPortfolioAccess}
      accessDeniedMessage={PORTFOLIO_ACCESS_DENIED_MESSAGE}
      isDataLoading={isDataLoading}
      isDataError={isDataError}
      dataErrorMessage={PORTFOLIO_DATA_ERROR_MESSAGE}
      groups={portfolioGroups}
      emptyStateMessage={PORTFOLIO_GROUP_EMPTY_STATE_MESSAGE}
    />
  );
};
